/**
 * Evidence sub-agent citation wire types. The Chat-bound modules that parse and
 * render these stay app-side; the schemas live here so a moved reader can name
 * them without importing that cone.
 */
import { z } from 'zod';

export const citationCandidateSchema = z.object({
  candidate_id: z.string(),
  source_id: z.string(),
  text: z.string(),
  provider_rank: z.number(),
  result_index: z.number(),
  // Kept as a plain string (not a zod enum) so a new BE strategy never fails the
  // whole payload parse; the value is display-only.
  strategy: z.string(),
});
export type CitationCandidateV1 = z.infer<typeof citationCandidateSchema>;

/**
 * Only `source_id` (the token join target) is load-bearing; other fields are
 * consumed defensively (`passthrough` preserves branding/display fields). The BE
 * keys each source by `id`, so normalize `id` → `source_id` before validating —
 * the rest of the model reads the single `source_id` join key.
 */
export const evidenceAgentSourceSchema = z.preprocess(
  (raw) => {
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
      const rec = raw as Record<string, unknown>;
      if (rec.source_id === undefined && typeof rec.id === 'string') {
        return { ...rec, source_id: rec.id };
      }
    }
    return raw;
  },
  z.object({ source_id: z.string() }).passthrough()
);
export type EvidenceAgentSource = z.infer<typeof evidenceAgentSourceSchema>;

// A malformed source must not void the rest of the array — validate elements
// individually and drop only the failures, preserving order.
const sourcesArraySchema = z
  .array(z.unknown())
  .default([])
  .transform((raw) =>
    raw
      .map((entry) => evidenceAgentSourceSchema.safeParse(entry))
      .filter(
        (result): result is { success: true; data: EvidenceAgentSource } => result.success
      )
      .map((result) => result.data)
  );

// Coerces null/empty/non-string to `undefined` so a sloppy optional field never
// voids an otherwise-valid image entry.
const optionalNonEmptyString = z.preprocess(
  (value) => (typeof value === 'string' && value.length > 0 ? value : undefined),
  z.string().optional()
);

const FIGURE_ID_RE = /^[0-9a-f]{64}$/;

/**
 * Tavily image (`https` url) XOR licensed partner figure (`figure_id`). Signed
 * asset URLs are never accepted — the FE blob-fetches figures by id.
 */
export const evidenceAgentImageSchema = z
  .object({
    url: z.preprocess(
      (value) =>
        typeof value === 'string' && value.startsWith('https://') ? value : undefined,
      z.string().optional()
    ),
    figure_id: z.preprocess(
      (value) =>
        typeof value === 'string' && FIGURE_ID_RE.test(value) ? value : undefined,
      z.string().regex(FIGURE_ID_RE).optional()
    ),
    title: optionalNonEmptyString,
    description: optionalNonEmptyString,
    // Anchor token the child can emit as `<img>TOKEN</img>`; absent on payloads
    // persisted before the BE started deriving it.
    image_id: optionalNonEmptyString,
  })
  .superRefine((image, ctx) => {
    const hasUrl = typeof image.url === 'string' && image.url.startsWith('https://');
    const hasFigure = typeof image.figure_id === 'string';
    if (hasUrl === hasFigure) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'exactly one of https url or figure_id is required',
      });
    }
  });
export type EvidenceAgentImage = z.infer<typeof evidenceAgentImageSchema>;

// Same resilience as sources: validate entries individually, drop only failures.
const imagesArraySchema = z
  .array(z.unknown())
  .default([])
  .transform((raw) =>
    raw
      .map((entry) => evidenceAgentImageSchema.safeParse(entry))
      .filter(
        (result): result is { success: true; data: EvidenceAgentImage } => result.success
      )
      .map((result) => result.data)
  );

export const evidenceAgentSearchPayloadSchema = z.object({
  // Pinned discriminator — a future breaking bump fails the parse and degrades to
  // "no payload" (generic row) instead of mis-rendering as v1.
  version: z.literal('1'),
  sources: sourcesArraySchema,
  citations: z.record(z.string()).default({}), // token -> source_id
  // Defaults to `{}` so payloads persisted before candidates existed still validate on reload.
  citation_candidates: z.record(z.array(citationCandidateSchema)).default({}),
  // Defaults to `[]` so payloads persisted before the typed images contract
  // still validate on reload.
  images: imagesArraySchema,
});
export type EvidenceAgentSearchPayloadV1 = z.infer<
  typeof evidenceAgentSearchPayloadSchema
>;

export interface CitationRegistry {
  /** token -> source_id (conflicting tokens are removed, not overwritten). */
  tokenToSourceId: Map<string, string>;
  sourcesById: Map<string, EvidenceAgentSource>;
  /** source_id -> candidates, best-first, deduped by candidate_id. */
  candidatesBySourceId: Map<string, CitationCandidateV1[]>;
  /** Tokens rejected because they mapped to >1 source_id across the child's searches. */
  conflictingTokens: Set<string>;
}

/** One resolved `<cite>` in answer order. The marker builders stay with the Chat cone. */
export interface ResolvedCitationOccurrence {
  token: string;
  sourceId: string;
  source: EvidenceAgentSource;
  /** Claim-selected preview candidate; undefined when the source has no candidates. */
  selectedCandidate?: CitationCandidateV1;
  /** Preceding clause used for claim-aware selection (display-agnostic; scoring only). */
  claimText: string;
  /** 0-based position among resolved occurrences, in answer order. */
  index: number;
}
